<template>
  <ion-page>
    <ion-header class="header-container">
      <ion-toolbar class="header-toolbar">
        <ion-buttons slot="start">
          <ion-button @click="goBack" fill="clear" class="back-button">
            <ion-icon
              slot="icon-only"
              :icon="chevronBack"
              class="back-icon"
            ></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title class="page-title-header">Menu Review</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="container">
        <div v-if="loading" class="loading-message">
          <ion-spinner></ion-spinner>
          <p>Loading menu...</p>
        </div>

        <div v-else-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <div v-else class="menu-content">
          <div v-if="availableCategories.length === 0" class="no-products">
            <p>No products found for this cafe.</p>
          </div>

          <div
            v-for="category in availableCategories"
            :key="category"
            class="category-section"
          >
            <div class="category-header" @click="toggleCategory(category)">
              <h2 class="category-title">{{ category }}</h2>
              <div class="category-header-right">
                <span class="category-count">
                  {{ productsByCategory[category].length }} items
                </span>
                <ion-icon
                  :icon="chevronDown"
                  class="category-chevron"
                  :class="{ 'chevron-rotated': !expandedCategories[category] }"
                ></ion-icon>
              </div>
            </div>

            <div
              :ref="'products-' + category"
              class="products-list"
              :style="getProductsListStyle(category)"
            >
              <div
                v-for="product in productsByCategory[category]"
                :key="product.uuid"
                class="product-card"
              >
                <div class="product-main">
                  <img
                    :src="product.productImgUrl || defaultImage"
                    class="product-thumb"
                    @error="handleImageError($event)"
                  />
                  <div class="product-info">
                    <h3 class="product-name">{{ product.name }}</h3>
                  </div>
                  <div class="product-price">
                    ${{ parseFloat(product.originalPrice).toFixed(2) }}
                  </div>
                </div>

                <div
                  v-if="hasVariations(product.variations)"
                  class="product-addons"
                >
                  <div class="addons-header">Add-ons & Options:</div>
                  <div class="addons-grid">
                    <div
                      v-for="(options, addonType) in getFilledVariations(
                        product.variations
                      )"
                      :key="addonType"
                      class="addon-group"
                    >
                      <div class="addon-type">
                        {{ formatAddonType(addonType) }}
                      </div>
                      <div class="addon-options">
                        <span
                          v-for="option in options"
                          :key="option.id || option.name"
                          class="addon-option"
                          :class="{ 'addon-default': option.isDefault }"
                        >
                          {{ option.name }}
                          <span v-if="option.price > 0" class="addon-price">
                            (+${{ parseFloat(option.price).toFixed(2) }})
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { defineComponent } from "vue";
import { chevronBack, chevronDown } from "ionicons/icons";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonTitle,
  IonContent,
  IonSpinner,
} from "@ionic/vue";
import axios from "axios";

const BASE_URI =
  process.env.VUE_APP_BASE_URI !== undefined
    ? process.env.VUE_APP_BASE_URI
    : "https://api.onecoffeeapp.com.au";

export default defineComponent({
  name: "AdminMenuReview",
  components: {
    IonPage,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonSpinner,
  },
  setup() {
    return {
      chevronBack,
      chevronDown,
    };
  },
  data() {
    return {
      loading: true,
      errorMessage: "",
      productsByCategory: {},
      categoryOrder: [],
      defaultImage: "assets/placeholders/coffee/latte.png",
      expandedCategories: {},
    };
  },
  computed: {
    availableCategories() {
      return this.categoryOrder.filter(
        (cat) =>
          this.productsByCategory[cat] &&
          this.productsByCategory[cat].length > 0
      );
    },
  },
  methods: {
    goBack() {
      this.$router.push("/admin");
    },
    toggleCategory(category) {
      this.expandedCategories = {
        ...this.expandedCategories,
        [category]: !this.expandedCategories[category],
      };
    },
    getProductsListStyle(category) {
      if (!this.expandedCategories[category]) {
        return { maxHeight: "0px", borderBottom: "none" };
      }
      // When expanded, don't limit height - let content determine size
      return { maxHeight: "none" };
    },
    formatAddonType(type) {
      // Capitalize first letter
      if (!type) return "";
      return type.charAt(0).toUpperCase() + type.slice(1);
    },
    hasVariations(variations) {
      if (!variations || typeof variations !== "object") return false;
      // Check if any variation category has options
      return Object.keys(variations).some(
        (key) => Array.isArray(variations[key]) && variations[key].length > 0
      );
    },
    getFilledVariations(variations) {
      // Return only variation categories that have options
      if (!variations || typeof variations !== "object") return {};
      const filled = {};
      for (const [key, options] of Object.entries(variations)) {
        if (Array.isArray(options) && options.length > 0) {
          filled[key] = options;
        }
      }
      return filled;
    },
    handleImageError(event) {
      if (event.target) {
        event.target.src = this.defaultImage;
      }
    },
    async loadMenuProducts() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const cafeId = localStorage.getItem("cafeId");

        if (!cafeId) {
          this.errorMessage = "No cafe associated with this account.";
          this.loading = false;
          return;
        }

        const response = await axios.get(
          `${BASE_URI}/product/get-cafe-products/${cafeId}`,
          {
            headers: {
              Authorization: localStorage.getItem("token"),
              "Content-Type": "application/json;charset=UTF-8",
            },
          }
        );

        if (!response.data.data || response.data.data.length === 0) {
          this.loading = false;
          return;
        }

        // Group products by category
        const categoryMap = {};
        const categoryOrderSet = new Set();

        for (const product of response.data.data) {
          const category = product.category || "Other";

          if (!categoryMap[category]) {
            categoryMap[category] = [];
            categoryOrderSet.add(category);
          }
          categoryMap[category].push(product);
        }

        this.productsByCategory = categoryMap;
        this.categoryOrder = Array.from(categoryOrderSet);
        // Initialize all categories as collapsed
        const expanded = {};
        for (const cat of this.categoryOrder) {
          expanded[cat] = false;
        }
        this.expandedCategories = expanded;
        this.loading = false;
      } catch (error) {
        console.error("Error loading menu:", error);
        this.errorMessage = "Failed to load menu. Please try again.";
        this.loading = false;
      }
    },
  },
  async mounted() {
    await this.loadMenuProducts();
  },
});
</script>

<style scoped>
/* Header Styles */
.header-container {
  --background: #f87e63;
}
.container {
  padding-top: 0px !important;
}

.header-toolbar {
  --background: #f87e63;
  --border-width: 0;
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 64px;
}

.page-title-header {
  --color: #ffffff;
  font-family: "Gilroy", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
}

.back-button {
  --padding-start: 0;
  --padding-end: 0;
  margin: 0;
}

.back-icon {
  color: #ffffff;
  font-size: 24px;
}

.container {
  padding: 12px 0;
}

.loading-message {
  text-align: center;
  padding: 40px 20px;
}

.loading-message p {
  margin-top: 10px;
  font-family: "Gilroy";
  color: #828282;
}

.error-message {
  padding: 15px;
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 8px;
  font-family: "Gilroy";
  font-size: 14px;
}

.no-products {
  text-align: center;
  padding: 40px 20px;
  font-family: "Gilroy";
  color: #828282;
}

/* Category Section */
.category-section {
  margin-bottom: 0;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px;
  background-color: #f87e63;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.category-header:active {
  background-color: #e86d52;
}

.category-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-chevron {
  color: #ffffff;
  font-size: 18px;
  transition: transform 0.3s ease;
}

.chevron-rotated {
  transform: rotate(-90deg);
}

.category-title {
  font-family: "Gilroy";
  font-weight: 600;
  font-size: 16px;
  color: #ffffff;
  margin: 0;
}

.category-count {
  font-family: "Gilroy";
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

/* Products List */
.products-list {
  background: #ffffff;
  border-top: none;
  border-bottom: 1px solid #f3f3f3;
  overflow: hidden;
  transition: max-height 0.35s ease-out, border-bottom 0.1s ease-out;
}

.product-card {
  padding: 12px;
  border-bottom: 1px solid #f3f3f3;
}

.product-card:last-child {
  border-bottom: none;
}

.product-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-thumb {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-family: "Gilroy";
  font-weight: 600;
  font-size: 14px;
  color: #1b1b1b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-price {
  font-family: "Gilroy";
  font-weight: 600;
  font-size: 14px;
  color: #f87e63;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Addons Section */
.product-addons {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e0e0e0;
}

.addons-header {
  font-family: "Gilroy";
  font-weight: 600;
  font-size: 11px;
  color: #828282;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.addons-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.addon-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
}

.addon-type {
  font-family: "Gilroy";
  font-weight: 600;
  font-size: 12px;
  color: #1b1b1b;
  min-width: 70px;
}

.addon-options {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
}

.addon-option {
  font-family: "Gilroy";
  font-size: 11px;
  color: #555;
  background-color: #f5f5f5;
  padding: 3px 6px;
  border-radius: 4px;
}

.addon-price {
  color: #f87e63;
  font-weight: 500;
}

.addon-default {
  background-color: #f87e63;
  color: #ffffff;
}

.addon-default .addon-price {
  color: #ffffff;
}

@media (max-width: 400px) {
  .addon-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .addon-type {
    min-width: auto;
  }
}
</style>
